import type { TodayWeekCell, TodayWeekSportScore } from "./types";
import { todayDateISOInTimeZone } from "./time";
import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";

type ForecastDay = {
  date?: string;
  score?: number | null;
  sportScores?: Record<string, number | null | undefined>;
  safetyState?: Record<string, ConditionSafetyState | null | undefined>;
};

export type TodayWeekSource = {
  slug: string;
  type: string;
  forecast: ForecastDay[];
};

/** Average each sport's best regional score per day, using only scored days. */
export function buildTodayWeekSportScores(
  sources: TodayWeekSource[],
  timezone: string,
  now: Date,
  allowedSports: Set<string>,
): TodayWeekSportScore[] {
  return [...allowedSports].flatMap((sportKey) => {
    const days = buildTodayWeek(sources, timezone, now, new Set([sportKey]))
      .flatMap(({ best }) => best ? [best] : []);
    if (days.length === 0) return [];
    const states = days.map((day) => day.safetyState ?? "no-data");
    const safetyState = states.includes("flagged-red") ? "flagged-red"
      : states.includes("flagged-amber") ? "flagged-amber"
        : states.includes("no-data") ? "no-data" : "clear";
    return [{
      sportKey,
      score: days.reduce((sum, day) => sum + day.score, 0) / days.length,
      days: days.length,
      safetyState,
    }];
  });
}

export function buildTodayWeek(
  sources: TodayWeekSource[],
  timezone: string,
  now: Date,
  allowedSports: Set<string>,
): TodayWeekCell[] {
  const todayISO = todayDateISOInTimeZone(timezone, now);

  return Array.from({ length: 7 }, (_, dayOffset) => {
    const dateISO = addDaysISO(todayISO, dayOffset);
    let best: TodayWeekCell["best"] = null;

    for (const source of sources) {
      const hasDatedDays = source.forecast.some((candidate) => Boolean(candidate.date));
      const day = hasDatedDays
        ? source.forecast.find((candidate) => candidate.date === dateISO)
        : source.forecast[dayOffset];
      if (!day) continue;

      const scores = mergedDayScores(source.type, day);
      for (const [sportKey, score] of Object.entries(scores)) {
        if (!allowedSports.has(sportKey)) continue;
        if (score === null || score === undefined || !Number.isFinite(score)) continue;
        const safetyState = daySafetyState(day, sportKey);
        // Phase 0 red-flag veto: a red-flagged reading is never a day's best, for every sport.
        if (safetyState === "flagged-red") continue;
        if (!best || score > best.score) {
          best = {
            sportKey,
            score,
            locationSlug: source.slug,
            ...(safetyState === undefined ? {} : { safetyState }),
          };
        }
      }
    }

    return {
      dayOffset,
      dateISO,
      weekday: weekdayLabel(dateISO),
      best,
    };
  });
}

/**
 * The day to recommend from a week: the cell with the highest `best.score` whose best is not
 * red-flagged, ties going to the earlier day. Null when no cell has an unflagged best.
 */
export function bestWeekCell(week: readonly TodayWeekCell[]): TodayWeekCell | null {
  let chosen: TodayWeekCell | null = null;
  for (const cell of week) {
    const best = cell.best;
    if (!best || !Number.isFinite(best.score) || best.safetyState === "flagged-red") continue;
    if (!chosen || best.score > (chosen.best?.score ?? Number.NEGATIVE_INFINITY)) chosen = cell;
  }
  return chosen;
}

function daySafetyState(
  day: ForecastDay,
  sportKey: string,
): ConditionSafetyState | undefined {
  const state = day.safetyState?.[sportKey] ?? (
    sportKey === "swim" ? day.safetyState?.swimming : undefined
  );
  if (state !== null && state !== undefined) return state;
  return isSupProfileKey(sportKey) ? "no-data" : undefined;
}

function mergedDayScores(type: string, day: ForecastDay): Record<string, number | null | undefined> {
  const scores: Record<string, number | null | undefined> = {};
  if (type === "ski") scores.piste = day.score;
  if (type === "surf") scores.surf = day.score;

  for (const [key, value] of Object.entries(day.sportScores ?? {})) {
    scores[key === "swimming" ? "swim" : key] = value;
  }

  return scores;
}

function addDaysISO(dateISO: string, days: number): string {
  const date = new Date(`${dateISO}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function weekdayLabel(dateISO: string): string {
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" }).format(
    new Date(`${dateISO}T12:00:00Z`),
  );
}
